import React from "react";
import { ClassNameProps, Column, dateTrunc, Grid, Heading, If, Row, Section, StyleProps } from "@hex-insights/core";
import { RichTextDisplay } from "../../../../Components/RichText";
import {
	Newsletter,
	NewsletterOrderField,
	OrderDirection,
	useNewsletterDisplayListQuery,
	useNewsletterSocialItemsQuery,
	User,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import * as ModelComponents from "../../_General";
import { SharingView } from "../../SharingRecord";
import * as SocialCommentComponents from "../../SocialComment";
import * as SocialReactionComponents from "../../SocialReaction";
import { NewsletterCard } from "../Card";
import { AuthorTimestampView } from "../General";
import { NewsletterUpdatePermissionRequired } from "../PermissionRequired";
import { NewsletterStatusBadge } from "../StatusBadge";
import { ControlledNewsletterUpdateModalController } from "../UpdateModal";
import styles from "./styles.module.css";

export type DetailDisplayNewsletter = Pick<Newsletter, "id" | "title" | "imageURL" | "body" | "releaseAt"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
};

export type NewsletterDetailDisplayProps = {
	newsletter: DetailDisplayNewsletter;
	withOtherNewsletters?: boolean;
	withStatus?: boolean;
	withAudience?: boolean;
	editable?: boolean;
	withReactions?: boolean;
	withComments?: boolean;
	isPreview?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function NewsletterDetailDisplay({
	newsletter,
	withOtherNewsletters = false,
	withStatus = false,
	withAudience = false,
	editable = false,
	withReactions = false,
	withComments = false,
	isPreview = false,
	className,
	style,
}: NewsletterDetailDisplayProps) {
	const { data: socialData } = useNewsletterSocialItemsQuery({
		variables: { id: newsletter.id },
		skip: !(withReactions || withComments),
	});

	const viewerReaction = socialData?.newsletter?.socialReactions.find((reaction) => reaction.author?.isCurrent) ?? null;
	const newsletterID = newsletter?.id;
	const reactionFormValues = React.useMemo(() => ({ newsletterID: newsletterID ?? null }), [newsletterID]);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<Column justify="spaced-start" style={{ marginBottom: "1.5rem" }}>
				<Column justify="spaced-start" verticalSpacing="0.25rem">
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							<AuthorTimestampView newsletter={newsletter} />

							<If condition={withStatus}>
								<NewsletterStatusBadge newsletter={newsletter} />
							</If>

							<If condition={editable && !isPreview}>
								<NewsletterUpdatePermissionRequired isAuthor={newsletter.author?.isCurrent ?? false}>
									<ControlledNewsletterUpdateModalController newsletterID={newsletter.id} variant="link" size="small">
										Edit
									</ControlledNewsletterUpdateModalController>
								</NewsletterUpdatePermissionRequired>
							</If>
						</Row>

						<If condition={withReactions}>
							<SocialReactionComponents.SocialReactionToggleButton
								instanceID={viewerReaction?.id}
								formValues={reactionFormValues}
								iconSize={24}
							/>
						</If>
					</Row>

					<If condition={withAudience}>
						<SharingView newsletterID={newsletter.id} className={styles["detail-display__audience"]} />
					</If>
				</Column>

				<ModelComponents.ImageWithModal
					src={newsletter.imageURL}
					alt={newsletter.title}
					sizing="cover"
					modalBackButtonText="Back to Newsletter"
					className={styles["detail-display__image"]}
					containerClassName={styles["detail-display__image__container"]}
				/>

				<If condition={withReactions}>
					<SocialReactionComponents.ReactionsView
						newsletterID={newsletter.id}
						reactions={socialData?.newsletter?.socialReactions ?? []}
					/>
				</If>
			</Column>

			<RichTextDisplay value={newsletter.body} className={styles["detail-display__body-text"]} />

			<If condition={withReactions}>
				<SocialReactionComponents.ReactionsView
					newsletterID={newsletter.id}
					reactions={socialData?.newsletter?.socialReactions ?? []}
					className={styles["detail-display__reactions--bottom"]}
				/>
			</If>

			<If condition={withComments}>
				<Section>
					<Section.Header>
						<Heading.H3 noMargin>Comments</Heading.H3>
					</Section.Header>
					<Section.Body>
						<SocialCommentComponents.CommentsView
							newsletterID={newsletter.id}
							comments={socialData?.newsletter?.socialComments ?? []}
						/>
					</Section.Body>
				</Section>
			</If>

			<If condition={withOtherNewsletters}>
				<OtherNewsletters newsletterID={newsletter.id} displayNum={3} />
			</If>
		</Column>
	);
}

const otherNewslettersResponsiveTracks = {
	0: 1,
	800: 3,
};

type OtherNewslettersProps = {
	newsletterID: Newsletter["id"];
	displayNum: number;
};

function OtherNewsletters({ newsletterID, displayNum }: OtherNewslettersProps) {
	const [now] = React.useState(() => dateTrunc(new Date(), "hour").toISOString()); // date trunc for improved caching
	const { data } = useNewsletterDisplayListQuery({
		variables: {
			filters: {
				releaseAtLTE: now,
				idNEQ: newsletterID,
			},
			order: { field: NewsletterOrderField.ReleaseAt, direction: OrderDirection.Desc },
			pagination: { first: displayNum },
		},
	});

	return (
		<Section>
			<Section.Header>
				<Heading.H3 noMargin>Recent Newsletters</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Grid responsiveTracks={otherNewslettersResponsiveTracks} gap="1rem">
					{data?.newsletterConnection.edges.map((e) => (
						<NewsletterCard key={e.node.id} newsletter={e.node} />
					))}
				</Grid>
			</Section.Body>
		</Section>
	);
}
